import React from 'react'
import classnames from 'classnames/bind'
import { useNavigate, generatePath } from '@caudena/core/Router/router'

import SeverityTag from '../../SeverityTag'
import Link from '@caudena/core/Link'
import { Avatar } from '@caudena/core/ui-kit'
import {
  Table2 as Table,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  createColumns,
} from '@caudena/core/ui-kit'
import { RowDeprecated } from '@caudena/core/ui-kit'
import { Typography } from '@caudena/core/ui-kit'
import { useFormatDate } from '../../../hooks'
import StatusDot from '../../StatusDot'

import CaseStatusTag from '../../CaseStatusTag'
import CaseDueDate from '../../CaseDueDate'
import { CasesDataItem } from '../../../types/Case'
import { observer } from 'mobx-react-lite'
import styles from './index.scss'

const cx = classnames.bind(styles)

const DEFAULT_PAGINATION_SIZE = 10

interface CaseTableProps {
  items?: Array<CasesDataItem>
  showClientId?: boolean
  sortBy?: string
  order?: 'asc' | 'desc'
  loading?: boolean
  onChangeSort?: (sortBy: string, order: 'asc' | 'desc') => void
}

const CaseTable = ({
  items,
  showClientId = true,
  sortBy,
  order,
  loading,
  onChangeSort,
}: CaseTableProps) => {
  const nav = useNavigate()

  const formatDate = useFormatDate()

  const { columns, columnsData } = createColumns<CasesDataItem>(
    items,
    [
      {
        title: 'case id',
        field: 'id',
        sortable: false,
        render: (item: CasesDataItem) => {
          const rightBadge = (
            <CaseStatusTag
              value={item.status}
              closedAs={item.closed_as}
              recommendAs={item.recommend_close_as}
              className={cx('IdRightBadge')}
            />
          )

          return (
            <RowDeprecated>
              <Link
                to={item.status === 'new' ? '/cases/:id/start' : '/cases/:id'}
                params={{ id: item.id }}
              >
                {item.id}
              </Link>
              {rightBadge}
            </RowDeprecated>
          )
        },
      },
      {
        title: 'label',
        field: 'label',
        sortable: false,
        render: (item: CasesDataItem) => <Typography>{item.tag}</Typography>,
      },
      {
        title: 'severity',
        field: 'severity',
        sortable: false,
        render: (item) => (
          <SeverityTag value={item.severity}>{item.severity}</SeverityTag>
        ),
      },
      {
        title: 'created',
        field: 'created_at',
        sortable: true,
        render: (item: CasesDataItem) =>
          item.created_at ? formatDate(new Date(item.created_at), 'date') : '',
      },
      {
        title: 'due date',
        field: 'due_date',
        sortable: true,
        render: (item: CasesDataItem) => (
          <CaseDueDate
            date={item.due_date ? new Date(item.due_date) : null}
            status={item.status}
          />
        ),
      },
      {
        title: 'client',
        field: 'client',
        sortable: false,
        render: (item: CasesDataItem) => (
          <RowDeprecated gap={0.5}>
            <StatusDot status={item.client.blocked ? 'blocked' : 'active'} />
            <Link
              className={cx('ClientIdLink')}
              to="/clients/:id"
              params={{ id: item.client.id }}
            >
              {item.client.id}
            </Link>
          </RowDeprecated>
        ),
      },
      {
        title: 'assignee',
        field: 'assignee',
        sortable: false,
        type: 'label' as const,
        render: (item: CasesDataItem) => (
          <Avatar
            className={cx('AssigneeAvatar')}
            src={item.assignee?.avatar || undefined}
            name={item.assignee?.name}
            size="sm"
          />
        ),
      },
    ].filter((col) => showClientId || col.field !== 'client'),
    {
      stubHeight: 70,
      stubAmount: DEFAULT_PAGINATION_SIZE,
    }
  )

  return (
    <Table loading={loading}>
      <Thead>
        <Tr>
          {columns.map(({ title, field, type, sortable }) => (
            <Th
              key={field}
              className={cx(field)}
              type={type}
              sortable={sortable}
              order={order}
              name={field}
              sortBy={sortBy}
              onChangeSort={onChangeSort}
            >
              {title}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {columnsData.map((item, index) => {
          const navigate = (e: React.MouseEvent) => {
            if (
              (e.target as HTMLElement)?.tagName === 'A' ||
              window.getSelection()?.type === 'Range'
            )
              return

            if (item.status === 'new') {
              nav(generatePath('/cases/:id/start', { id: item.id }))
            } else {
              nav(generatePath('/cases/:id', { id: item.id }))
            }
          }

          return (
            <Tr
              key={`columnsData_${index}`}
              className={cx('ClickableRow')}
              onClick={navigate}
            >
              {columns.map(({ field, render, type }) => (
                <Td key={field} className={cx('col', field)} type={type}>
                  {render(item)}
                </Td>
              ))}
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

export default observer(CaseTable)
